import { useState, useMemo } from 'react';
import type { Word } from '../types/word.types';

export function useWordSelection(filteredWords: Word[]) {
  const [selectedWords, setSelectedWords] = useState<Set<string>>(new Set());

  // Get the set of valid word strings from filtered words (normalized to lowercase)
  const validWordStrings = useMemo(() => {
    return new Set(filteredWords.map(w => w.word.toLowerCase()));
  }, [filteredWords]);

  // Only clear selections when explicitly requested (not automatically on word list changes)

  // Filter selected words to only include valid ones (case-insensitive)
  const validSelectedWords = useMemo(() => {
    const valid = new Set<string>();
    for (const wordString of selectedWords) {
      if (validWordStrings.has(wordString.toLowerCase())) {
        valid.add(wordString);
      }
    }
    return valid;
  }, [selectedWords, validWordStrings]);

  const toggleWordSelection = (wordString: string) => {
    // Normalize to lowercase for consistent storage
    const normalizedWord = wordString.toLowerCase();

    setSelectedWords(prev => {
      const newSet = new Set(prev);
      if (newSet.has(normalizedWord)) {
        // Allow deselection
        newSet.delete(normalizedWord);
      } else {
        // Only allow selection if we haven't reached the limit of 5
        if (newSet.size < 5) {
          newSet.add(normalizedWord);
        }
      }
      return newSet;
    });
  };

  const selectAllWords = () => {
    const wordsToSelect = filteredWords.slice(0, 5); // Limit to 5 words max
    setSelectedWords(new Set(wordsToSelect.map(word => word.word.toLowerCase())));
  };

  const clearSelection = () => {
    setSelectedWords(new Set());
  };

  const randomizeSelection = (count: number) => {
    const availableWords = filteredWords;
    const actualCount = Math.min(count, availableWords.length, 5); // Cap at 5 words max
    const shuffled = [...availableWords].sort(() => Math.random() - 0.5);
    const selected = shuffled.slice(0, actualCount);
    setSelectedWords(new Set(selected.map(word => word.word.toLowerCase())));
  };

  const getSelectedWords = () => {
    // Create a map of normalized word strings to word objects for lookup
    const wordMap = new Map<string, Word>();
    filteredWords.forEach(word => {
      wordMap.set(word.word.toLowerCase(), word);
    });

    // Return the actual word objects for selected normalized words
    return Array.from(selectedWords)
      .map(normalizedWord => wordMap.get(normalizedWord))
      .filter((word): word is Word => word !== undefined);
  };

  return {
    selectedWords: validSelectedWords,
    toggleWordSelection,
    selectAllWords,
    clearSelection,
    randomizeSelection,
    getSelectedWords,
  };
}
